import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SettingsIcon from "@material-ui/icons/Settings";
import CropFreeIcon from "@material-ui/icons/CropFree";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { ZoneMode, GameMode } from "../../../constants/TourConstants";
import ApiZone from "../../../API/ApiZone";
import ApiEnigma from "../../../API/ApiEnigma";
import ApiTour from "../../../API/ApiTour";
import DialogWarning from "../dialogWarning";

export const HIERARCHY_WIDTH = 280;

/**
 * In-memory cache keyed by tourId so the hierarchy renders instantly when
 * re-mounted on navigation (parcours <-> zone <-> enigme). The three pages
 * hosting the hierarchy are remounted on every query-string change, so
 * without this cache each navigation refetched the whole tree.
 *
 * The whole tree comes from a single `GET /tourOutline` call. Mutations done
 * from the app dispatch `tourHierarchy:refresh*` events, which force a
 * reload, so a long staleness window is safe: it only covers edits made
 * outside this tab.
 */
const OUTLINE_STALE_MS = 60 * 1000;

/**
 * Scroll offset of the sidebar, kept per tour across remounts so that
 * navigating enigme -> enigme does not jump back to the top.
 */
const scrollTopByTour = {};

const outlineCacheByTour = {};
const outlineInflightByTour = {};

/** null = unknown, false = back-end without /tourOutline (legacy fallback). */
let outlineRouteAvailable = null;

const toEnigmaItem = (e) => ({
  id: e.id,
  title: e.title,
  orderId: e.orderId ?? 0,
});

/** Legacy path: 1 call for the tour, 1 for the zones, then 1 per zone. */
async function fetchOutlineLegacy(tourId) {
  const [tourRes, zonesRes] = await Promise.all([
    ApiTour.getTour(tourId),
    ApiZone.getZones(tourId),
  ]);

  const tour =
    tourRes && tourRes.status === 200 && tourRes.data ? tourRes.data : null;
  const zones =
    zonesRes && zonesRes.status === 200 ? zonesRes.data || [] : [];

  const enigmasByZone = {};
  await Promise.all(
    zones.map((zone) =>
      ApiEnigma.getEnigmas(zone.id).then((res) => {
        enigmasByZone[String(zone.id)] =
          res && res.status === 200 && res.data
            ? res.data.map(toEnigmaItem)
            : [];
      })
    )
  );

  return { tour, zones, enigmasByZone };
}

/** Single aggregated call, with a graceful fallback if the route is absent. */
async function fetchOutline(tourId) {
  if (outlineRouteAvailable === false) return fetchOutlineLegacy(tourId);

  const res = await ApiTour.getTourOutline(tourId);

  if (res && res.status === 404) {
    outlineRouteAvailable = false;
    return fetchOutlineLegacy(tourId);
  }
  if (!res || res.status !== 200 || !res.data) return null;

  outlineRouteAvailable = true;

  const data = res.data;
  const enigmasByZone = {};
  const zones = (data.zones || []).map(({ enigmas, ...zone }) => {
    enigmasByZone[String(zone.id)] = (enigmas || []).map(toEnigmaItem);
    return zone;
  });

  return { tour: data, zones, enigmasByZone };
}

/**
 * Loads the outline, de-duplicating concurrent calls so remounting the
 * hierarchy mid-flight costs no extra request.
 */
function loadOutline(tourId, { force = false } = {}) {
  const key = String(tourId);
  if (!force && outlineInflightByTour[key]) return outlineInflightByTour[key];

  const request = fetchOutline(tourId)
    .then((outline) => {
      if (outline) {
        outlineCacheByTour[key] = { ...outline, fetchedAt: Date.now() };
      }
      return outline;
    })
    .catch(() => null)
    .finally(() => {
      if (outlineInflightByTour[key] === request) {
        delete outlineInflightByTour[key];
      }
    });

  outlineInflightByTour[key] = request;
  return request;
}

const actionButtonBase = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 26,
  height: 26,
  flexShrink: 0,
  borderRadius: 6,
  opacity: 0,
  transition: "opacity 0.15s, background 0.15s, color 0.15s",
};

const useStyles = makeStyles((theme) => ({
  root: {
    position: "fixed",
    top: 0,
    left: 0,
    width: HIERARCHY_WIDTH,
    height: "100vh",
    backgroundColor: "#FFFFFF",
    borderRight: "1px solid #E2E8F0",
    display: "flex",
    flexDirection: "column",
    zIndex: 1100,
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: 2 },
    [theme.breakpoints.down("xs")]: { display: "none" },
  },
  header: {
    padding: "14px 16px 10px",
    borderBottom: "1px solid #E2E8F0",
    flexShrink: 0,
    backgroundColor: "#FFFFFF",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: "#64748B",
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 10,
    padding: "2px 4px",
    borderRadius: 4,
    transition: "color 0.15s, background 0.15s",
    "&:hover": {
      color: "#3B82F6",
      backgroundColor: "#EFF6FF",
      textDecoration: "none",
    },
  },
  tourTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 6,
  },
  tourIcon: { fontSize: 20 },
  tourTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#0F172A",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#94A3B8",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: 6,
    marginTop: 2,
  },
  statsRow: { marginBottom: 2 },
  statsText: { fontSize: 13, fontWeight: 600, color: "#1E293B" },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    padding: "6px 0 4px",
    "&::-webkit-scrollbar": { width: 4 },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: 2 },
  },
  /* ---------- Config row ---------- */
  configRow: {
    display: "flex",
    alignItems: "center",
    padding: "7px 10px",
    cursor: "pointer",
    borderRadius: 8,
    margin: "1px 8px 4px",
    textDecoration: "none",
    transition: "background-color 0.15s",
    "&:hover": { backgroundColor: "#F1F5F9" },
  },
  configRowActive: {
    backgroundColor: "#DBEAFE",
    "&:hover": { backgroundColor: "#BFDBFE" },
  },
  configIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: "#1E3A5F",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    flexShrink: 0,
  },
  configTextWrapper: { flex: 1, overflow: "hidden" },
  configLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#1E293B",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  configSubLabel: {
    fontSize: 11,
    color: "#94A3B8",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    marginTop: 1,
  },
  /* ---------- Zone row ---------- */
  zoneHeader: {
    display: "flex",
    alignItems: "center",
    borderRadius: 8,
    margin: "1px 8px",
    cursor: "grab",
    transition: "background-color 0.15s",
    "&:active": { cursor: "grabbing" },
    "&:hover": {
      backgroundColor: "#F1F5F9",
      "& $zoneDeleteButton": { opacity: 1 },
    },
  },
  zoneHeaderActive: {
    backgroundColor: "#FFF7ED",
    "&:hover": { backgroundColor: "#FFEDD5" },
  },
  chevronButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 36,
    flexShrink: 0,
    cursor: "pointer",
    borderRadius: 6,
    "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
  },
  expandIcon: {
    fontSize: 18,
    color: "#94A3B8",
    transition: "transform 0.2s",
  },
  zoneLink: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    overflow: "hidden",
    textDecoration: "none",
    padding: "7px 0 7px 2px",
    minWidth: 0,
  },
  zoneIconBox: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    flexShrink: 0,
    backgroundColor: "#64748B",
    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  },
  zoneInfo: { flex: 1, overflow: "hidden", minWidth: 0 },
  zoneName: {
    fontSize: 13,
    fontWeight: 600,
    color: "#1E293B",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  zoneMeta: { fontSize: 11, color: "#94A3B8", marginTop: 1 },
  zoneMetaInactive: { fontSize: 11, color: "#EF4444", marginTop: 1 },
  zoneBadge: {
    fontSize: 10,
    fontWeight: 700,
    backgroundColor: "#F1F5F9",
    color: "#475569",
    padding: "2px 6px",
    borderRadius: 4,
    marginLeft: 4,
    flexShrink: 0,
    border: "1px solid #E2E8F0",
  },
  zoneDeleteButton: {
    ...actionButtonBase,
    cursor: "pointer",
    color: "#94A3B8",
    margin: "0 6px 0 2px",
    "&:hover": {
      backgroundColor: "rgba(239, 68, 68, 0.1)",
      color: "#EF4444",
    },
  },
  /* ---------- Enigma rows ---------- */
  enigmaList: { marginBottom: 2 },
  enigmaRow: {
    display: "flex",
    alignItems: "center",
    borderRadius: 8,
    margin: "1px 8px",
    paddingLeft: 38,
    cursor: "grab",
    transition: "background-color 0.15s",
    "&:active": { cursor: "grabbing" },
    "&:hover": {
      backgroundColor: "#F1F5F9",
      "& $enigmaDeleteButton": { opacity: 1 },
    },
  },
  enigmaRowActive: {
    backgroundColor: "#EFF6FF",
    "&:hover": { backgroundColor: "#DBEAFE" },
  },
  enigmaLink: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    overflow: "hidden",
    textDecoration: "none",
    padding: "5px 0 5px 6px",
    minWidth: 0,
  },
  enigmaNumber: {
    fontSize: 11,
    color: "#94A3B8",
    width: 16,
    flexShrink: 0,
    marginRight: 6,
    textAlign: "right",
  },
  enigmaTitle: {
    fontSize: 12,
    color: "#334155",
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  enigmaTitleActive: { color: "#1D4ED8", fontWeight: 600 },
  enigmaDeleteButton: {
    ...actionButtonBase,
    cursor: "pointer",
    color: "#94A3B8",
    margin: "0 6px 0 2px",
    "&:hover": {
      backgroundColor: "rgba(239, 68, 68, 0.1)",
      color: "#EF4444",
    },
  },
  addEnigmaLink: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: "5px 10px 5px 44px",
    margin: "1px 8px",
    fontSize: 12,
    color: "#3B82F6",
    fontWeight: 500,
    cursor: "pointer",
    borderRadius: 8,
    textDecoration: "none",
    transition: "background-color 0.15s",
    "&:hover": { backgroundColor: "#EFF6FF" },
  },
  addEnigmaDisabled: {
    color: "#94A3B8",
    cursor: "not-allowed",
    "&:hover": { backgroundColor: "transparent" },
  },
  enigmaLoadingRow: { display: "flex", justifyContent: "center", padding: "8px 0" },
  /* ---------- Footer ---------- */
  footer: {
    padding: "10px 16px 14px",
    borderTop: "1px solid #E2E8F0",
    flexShrink: 0,
    backgroundColor: "#FFFFFF",
    position: "sticky",
    bottom: 0,
  },
  addZoneButton: {
    width: "100%",
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    padding: "7px 0",
    backgroundColor: "#F1F5F9",
    color: "#475569",
    border: "1px solid #E2E8F0",
    "&:hover": { backgroundColor: "#E2E8F0" },
  },
  addZoneButtonDisabled: {
    backgroundColor: "#F8FAFC",
    color: "#CBD5E1",
    border: "1px solid #F1F5F9",
    "&:hover": { backgroundColor: "#F8FAFC" },
  },
}));

function TourHierarchyComponent({
  tourId,
  tourTitle: tourTitleProp,
  currentZoneId,
  currentEnigmaId,
  isConfigActive,
  role,
  tourDisabled,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const [tourTitle, setTourTitle] = useState(tourTitleProp || "");
  const [tourGameMode, setTourGameMode] = useState(null);
  const [tourTimer, setTourTimer] = useState(null);
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(true);
  const [enigmasByZone, setEnigmasByZone] = useState({});
  const [loadingEnigmasForZone, setLoadingEnigmasForZone] = useState({});

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    id: null,
    zoneId: null,
  });

  const loadedZonesRef = useRef(new Set());
  const loadingZonesRef = useRef(new Set());
  const tourTitlePropRef = useRef(tourTitleProp);
  const mountedRef = useRef(true);
  const rootRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const scrollRestoredForRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* ---- Preserve the sidebar scroll position across navigations ----
   * The pages hosting the hierarchy are remounted on every query-string
   * change, which resets the scroll to the top. We remember the offset per
   * tour and restore it before the first paint of the new page.
   *
   * `scrollArea` is the scroller (a flex item with its own overflow), but we
   * fall back to `root` so the behaviour survives a layout change.
   */
  const getScrollEl = useCallback(() => {
    const area = scrollAreaRef.current;
    if (area && area.scrollHeight > area.clientHeight) return area;
    const root = rootRef.current;
    if (root && root.scrollHeight > root.clientHeight) return root;
    return null;
  }, []);

  useEffect(() => {
    if (!tourId) return;
    const elements = [scrollAreaRef.current, rootRef.current].filter(Boolean);
    const handleScroll = (event) => {
      scrollTopByTour[String(tourId)] = event.currentTarget.scrollTop;
    };
    elements.forEach((el) =>
      el.addEventListener("scroll", handleScroll, { passive: true })
    );
    return () =>
      elements.forEach((el) => el.removeEventListener("scroll", handleScroll));
  }, [tourId]);

  /* Runs on every render until the tree is tall enough to be scrolled, since
   * the content only appears once the outline has been applied.
   *
   * Keyed on the tour we actually restored for, not on a boolean: pages can
   * render once with a placeholder tourId before reading the query string
   * (newZone starts at `null`), and that first pass must not consume the
   * restoration.
   */
  useLayoutEffect(() => {
    if (!tourId) return;

    const key = String(tourId);
    if (scrollRestoredForRef.current === key) return;

    const saved = scrollTopByTour[key];
    if (!saved) {
      scrollRestoredForRef.current = key;
      return;
    }

    const el = getScrollEl();
    if (!el) return;

    el.scrollTop = saved;
    scrollRestoredForRef.current = key;
  });

  /* ---- localStorage helpers ---- */
  const getStorageKey = useCallback(
    () => `tourHierarchy_expanded_${tourId}`,
    [tourId]
  );

  const getStoredExpanded = useCallback(() => {
    if (!tourId) return {};
    try {
      const stored = localStorage.getItem(getStorageKey());
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }, [tourId, getStorageKey]);

  const [expandedZones, setExpandedZones] = useState(getStoredExpanded);

  useEffect(() => {
    if (!tourId) return;
    setExpandedZones(getStoredExpanded());
  }, [tourId, getStoredExpanded]);

  /* ---- Apply a loaded outline to the local state ---- */
  const applyOutline = useCallback((outline) => {
    if (!outline || !mountedRef.current) return;

    if (outline.tour) {
      if (!tourTitlePropRef.current) setTourTitle(outline.tour.title || "");
      setTourGameMode(outline.tour.gameMode ?? null);
      setTourTimer(outline.tour.timer ?? null);
    }

    setZones(outline.zones || []);
    setEnigmasByZone(outline.enigmasByZone || {});
    Object.keys(outline.enigmasByZone || {}).forEach((k) =>
      loadedZonesRef.current.add(k)
    );
    setZonesLoading(false);
  }, []);

  /* ---- Load the whole tree (cache first, single network call) ----
   * Deliberately keyed on tourId only: tourTitleProp changes on every
   * keystroke in the tour title field, and must never trigger a reload.
   */
  useEffect(() => {
    if (!tourId) {
      setZonesLoading(false);
      return;
    }

    const cached = outlineCacheByTour[String(tourId)];
    if (cached) {
      applyOutline(cached);
    } else {
      setZonesLoading(true);
    }

    const isStale = !cached || Date.now() - cached.fetchedAt > OUTLINE_STALE_MS;
    if (isStale) {
      loadOutline(tourId).then((outline) => {
        if (outline) applyOutline(outline);
        else if (mountedRef.current) setZonesLoading(false);
      });
    }
  }, [tourId, applyOutline]);

  /* ---- Keep the cache in sync with local mutations (delete, reorder) ---- */
  useEffect(() => {
    if (!tourId || zonesLoading) return;
    const cached = outlineCacheByTour[String(tourId)];
    if (!cached) return;
    outlineCacheByTour[String(tourId)] = { ...cached, zones, enigmasByZone };
  }, [tourId, zones, enigmasByZone, zonesLoading]);

  useEffect(() => {
    tourTitlePropRef.current = tourTitleProp;
    if (tourTitleProp) setTourTitle(tourTitleProp);
  }, [tourTitleProp]);

  /* ---- Load enigmas for a single zone ----
   * Only used for a targeted refresh (after a save) or when a zone has no
   * data yet; the normal path gets every zone from the outline call.
   */
  const loadEnigmasForZone = useCallback((zoneId) => {
    const key = String(zoneId);
    if (loadingZonesRef.current.has(key)) return;
    const alreadyLoaded = loadedZonesRef.current.has(key);
    loadingZonesRef.current.add(key);
    if (!alreadyLoaded) {
      setLoadingEnigmasForZone((prev) => ({ ...prev, [key]: true }));
    }
    ApiEnigma.getEnigmas(zoneId).then((res) => {
      let list = [];
      if (res && res.status === 200 && res.data) {
        list = res.data.map(toEnigmaItem);
      }
      loadedZonesRef.current.add(key);
      loadingZonesRef.current.delete(key);
      if (!mountedRef.current) return;
      setEnigmasByZone((prev) => ({ ...prev, [key]: list }));
      setLoadingEnigmasForZone((prev) => ({ ...prev, [key]: false }));
    });
  }, []);

  /* ---- Auto-expand active zone ---- */
  useEffect(() => {
    if (!currentZoneId || !tourId) return;
    const key = String(currentZoneId);
    setExpandedZones((prev) => {
      if (prev[key]) return prev;
      const next = { ...prev, [key]: true };
      try { localStorage.setItem(getStorageKey(), JSON.stringify(next)); } catch { }
      return next;
    });
  }, [currentZoneId, tourId, getStorageKey]);

  /* ---- Expand/collapse toggle ---- */
  const toggleZone = useCallback(
    (zoneId) => {
      const key = String(zoneId);
      setExpandedZones((prev) => {
        const willOpen = !prev[key];
        const next = { ...prev, [key]: willOpen };
        try { localStorage.setItem(getStorageKey(), JSON.stringify(next)); } catch { }
        if (willOpen && !loadedZonesRef.current.has(key)) {
          loadEnigmasForZone(zoneId);
        }
        return next;
      });
    },
    [getStorageKey, loadEnigmasForZone]
  );

  const ensureExpanded = useCallback(
    (zoneId) => {
      const key = String(zoneId);
      setExpandedZones((prev) => {
        if (prev[key]) return prev;
        const next = { ...prev, [key]: true };
        try { localStorage.setItem(getStorageKey(), JSON.stringify(next)); } catch { }
        if (!loadedZonesRef.current.has(key)) loadEnigmasForZone(zoneId);
        return next;
      });
    },
    [getStorageKey, loadEnigmasForZone]
  );

  /* ---- Refresh helpers (after create from edit pages) ---- */
  const refreshZones = useCallback(() => {
    if (!tourId) return;
    loadOutline(tourId, { force: true }).then(applyOutline);
  }, [tourId, applyOutline]);

  const refreshEnigmasForZone = useCallback(
    (zoneId) => {
      loadingZonesRef.current.delete(String(zoneId));
      loadEnigmasForZone(zoneId);
    },
    [loadEnigmasForZone]
  );

  useEffect(() => {
    const handleRefreshZones = (e) => {
      refreshZones();
      const zid = e && e.detail ? e.detail.zoneId : null;
      if (zid != null) ensureExpanded(zid);
    };
    const handleRefreshEnigmas = (e) => {
      const zid = e && e.detail ? e.detail.zoneId : null;
      if (zid != null) {
        ensureExpanded(zid);
        refreshEnigmasForZone(zid);
      }
    };
    window.addEventListener("tourHierarchy:refreshZones", handleRefreshZones);
    window.addEventListener("tourHierarchy:refreshEnigmas", handleRefreshEnigmas);
    return () => {
      window.removeEventListener("tourHierarchy:refreshZones", handleRefreshZones);
      window.removeEventListener("tourHierarchy:refreshEnigmas", handleRefreshEnigmas);
    };
  }, [refreshZones, refreshEnigmasForZone, ensureExpanded]);

  /* ---- Drag & drop reorder ---- */
  const handleDragEnd = useCallback(
    async ({ source, destination }) => {
      if (!destination) return;
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) return;

      if (source.droppableId === "zones") {
        const newZones = [...zones];
        const [removed] = newZones.splice(source.index, 1);
        newZones.splice(destination.index, 0, removed);
        newZones.forEach((z, i) => { z.orderId = i; });
        setZones(newZones);
        await ApiZone.updateZonesOrdersIds(newZones);
      } else if (source.droppableId.startsWith("enigmas-")) {
        const zoneKey = source.droppableId.replace("enigmas-", "");
        const newEnigmas = [...(enigmasByZone[zoneKey] || [])];
        const [removed] = newEnigmas.splice(source.index, 1);
        newEnigmas.splice(destination.index, 0, removed);
        newEnigmas.forEach((e, i) => { e.orderId = i; });
        setEnigmasByZone((prev) => ({ ...prev, [zoneKey]: newEnigmas }));
        await ApiEnigma.updateEnigmaOrdersIds(newEnigmas);
      }
    },
    [zones, enigmasByZone]
  );

  /* ---- Delete dialog handlers ---- */
  const openDeleteDialog = useCallback((type, id, zoneId = null) => {
    setDeleteDialog({ open: true, type, id, zoneId });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const handleConfirmDelete = useCallback(
    async (id) => {
      const { type, zoneId } = deleteDialog;
      closeDeleteDialog();
      if (type === "zone") {
        await ApiZone.deleteZone(id);
        setZones((prev) => prev.filter((z) => String(z.id) !== String(id)));
        setEnigmasByZone((prev) => {
          const next = { ...prev };
          delete next[String(id)];
          return next;
        });
        loadedZonesRef.current.delete(String(id));
        loadingZonesRef.current.delete(String(id));
        if (String(currentZoneId) === String(id)) {
          history.push(`/parcours?id=${tourId}`);
        }
      } else if (type === "enigma") {
        await ApiEnigma.deleteEnigma(id);
        setEnigmasByZone((prev) => ({
          ...prev,
          [String(zoneId)]: (prev[String(zoneId)] || []).filter(
            (e) => String(e.id) !== String(id)
          ),
        }));
        if (String(currentEnigmaId) === String(id)) {
          history.push(`/zone?tourId=${tourId}&zoneId=${zoneId}`);
        }
      }
    },
    [deleteDialog, closeDeleteDialog, currentZoneId, currentEnigmaId, tourId, history]
  );

  /* ---- Computed values ---- */
  const totalEnigmas = Object.values(enigmasByZone).reduce(
    (sum, list) => sum + list.length,
    0
  );
  const canAddZone = tourId && !tourDisabled && role !== "NO_SUBSCRIBTION";

  const getZoneModeBadge = (zoneMode) => {
    if (zoneMode === ZoneMode.MAP) return "GPS";
    if (zoneMode === ZoneMode.QRCODE) return "QR";
    if (zoneMode === ZoneMode.AUTOMATIC_LAUNCH) return "AUTO";
    return "QR";
  };

  const getGameModeLabel = () => {
    if (tourGameMode === GameMode.ONLY_QRCODE) return t("newTour.only_qr_codes");
    if (tourGameMode === GameMode.MAP_AND_QRCODE) return t("newTour.map_and_qr_codes");
    if (tourGameMode === GameMode.AUTOMATIC_LAUNCH) return t("newTour.automatic_launch");
    return null;
  };

  const configSubtitle = (() => {
    const parts = [];
    const modeLabel = getGameModeLabel();
    if (modeLabel) parts.push(modeLabel);
    if (tourTimer) parts.push(`${tourTimer} min`);
    return parts.join(" · ");
  })();

  return (
    <Box className={classes.root} ref={rootRef}>
      {/* ---- Header ---- */}
      <Box className={classes.header}>
        <Link to="/" className={classes.backLink}>
          <ArrowBackIcon style={{ fontSize: 14 }} />
          <span>{t("tourHierarchy.back_to_list")}</span>
        </Link>
        {tourTitle && (
          <Box className={classes.tourTitleRow}>
            <Typography className={classes.tourTitle} title={tourTitle}>
              {tourTitle}
            </Typography>
          </Box>
        )}
        <Typography className={classes.sectionLabel}>
          {t("tourHierarchy.structure_label")}
        </Typography>
        <Box className={classes.statsRow}>
          <Typography className={classes.statsText}>
            {zones.length} {t("tourHierarchy.zones_count")} &middot;{" "}
            {totalEnigmas} {t("tourHierarchy.enigmas_count")}
          </Typography>
        </Box>
      </Box>

      {/* ---- Scroll area ---- */}
      <Box className={classes.scrollArea} ref={scrollAreaRef}>
        {/* Configuration du parcours */}
        {tourId && (
          <Link
            to={`/parcours?id=${tourId}`}
            className={`${classes.configRow} ${isConfigActive ? classes.configRowActive : ""}`}
            style={{ textDecoration: "none" }}
          >
            <Box className={classes.configIcon}>
              <SettingsIcon style={{ fontSize: 20, color: "white" }} />
            </Box>
            <Box className={classes.configTextWrapper}>
              <Typography className={classes.configLabel}>
                {t("tourHierarchy.config_label")}
              </Typography>
              {configSubtitle ? (
                <Typography className={classes.configSubLabel}>
                  {configSubtitle}
                </Typography>
              ) : null}
            </Box>
          </Link>
        )}

        {/* Zones list with drag & drop */}
        {zonesLoading ? (
          <Box style={{ display: "flex", justifyContent: "center", padding: 16 }}>
            <CircularProgress size={20} style={{ color: "#94A3B8" }} />
          </Box>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="zones" type="ZONE">
              {(droppableProvided) => (
                <div
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {zones.map((zone, index) => {
                    const isExpanded = !!expandedZones[String(zone.id)];
                    const isActiveZone =
                      currentZoneId != null &&
                      String(currentZoneId) === String(zone.id);
                    const enigmas = enigmasByZone[String(zone.id)] || [];
                    const isLoadingEnigmas = !!loadingEnigmasForZone[String(zone.id)];
                    const canAddEnigma = zone.id && canAddZone;
                    const badge = getZoneModeBadge(zone.zoneMode);
                    const enigmaCount = loadedZonesRef.current.has(String(zone.id))
                      ? enigmas.length
                      : null;

                    const metaText = (() => {
                      const parts = [];
                      if (enigmaCount !== null) {
                        parts.push(`${enigmaCount} ${t("tourHierarchy.enigmas_count")}`);
                      }
                      if (zone.timer) parts.push(`${zone.timer} min`);
                      if (zone.isActive === false) {
                        return (
                          <Typography className={classes.zoneMetaInactive}>
                            {parts.length > 0
                              ? `${parts.join(" · ")} · ${t("tourHierarchy.zone_inactive")}`
                              : t("tourHierarchy.zone_inactive")}
                          </Typography>
                        );
                      }
                      return parts.length > 0 ? (
                        <Typography className={classes.zoneMeta}>
                          {parts.join(" · ")}
                        </Typography>
                      ) : null;
                    })();

                    return (
                      <Draggable
                        key={String(zone.id)}
                        draggableId={"zone-" + zone.id}
                        index={index}
                      >
                        {(draggableProvided) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                          >
                            <Box
                              className={`${classes.zoneHeader} ${isActiveZone ? classes.zoneHeaderActive : ""}`}
                              {...draggableProvided.dragHandleProps}
                            >
                              {/* Chevron toggle */}
                              <Box
                                className={classes.chevronButton}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleZone(zone.id);
                                }}
                              >
                                <ExpandMoreIcon
                                  className={classes.expandIcon}
                                  style={{
                                    transform: isExpanded
                                      ? "rotate(0deg)"
                                      : "rotate(-90deg)",
                                  }}
                                />
                              </Box>

                              {/* Zone link */}
                              <Link
                                to={`/zone?tourId=${tourId}&zoneId=${zone.id}`}
                                className={classes.zoneLink}
                                onClick={() => ensureExpanded(zone.id)}
                              >
                                <Box className={classes.zoneIconBox}>
                                  {zone.zoneMode === ZoneMode.MAP ? (
                                    <LocationOnIcon style={{ fontSize: 16, color: "white" }} />
                                  ) : (
                                    <CropFreeIcon style={{ fontSize: 16, color: "white" }} />
                                  )}
                                </Box>
                                <Box className={classes.zoneInfo}>
                                  <Typography className={classes.zoneName} title={zone.name}>
                                    {zone.name || t("tourHierarchy.unnamed_zone")}
                                  </Typography>
                                  {metaText}
                                </Box>
                              </Link>

                              {/* Mode badge */}
                              <Typography className={classes.zoneBadge}>{badge}</Typography>

                              {/* Delete button (hover only) */}
                              <Box
                                className={classes.zoneDeleteButton}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openDeleteDialog("zone", zone.id);
                                }}
                              >
                                <DeleteOutlineIcon style={{ fontSize: 15 }} />
                              </Box>
                            </Box>

                            {/* Enigma list */}
                            {isExpanded && (
                              <Box className={classes.enigmaList}>
                                {isLoadingEnigmas && (
                                  <Box className={classes.enigmaLoadingRow}>
                                    <CircularProgress size={14} style={{ color: "#94A3B8" }} />
                                  </Box>
                                )}
                                <Droppable
                                  droppableId={"enigmas-" + zone.id}
                                  type="ENIGMA"
                                >
                                  {(enigmaDroppable) => (
                                    <div
                                      ref={enigmaDroppable.innerRef}
                                      {...enigmaDroppable.droppableProps}
                                    >
                                      {enigmas.map((enigma, enigmaIndex) => {
                                        const isActiveEnigma =
                                          currentEnigmaId != null &&
                                          String(currentEnigmaId) === String(enigma.id);
                                        return (
                                          <Draggable
                                            key={String(enigma.id)}
                                            draggableId={"enigma-" + enigma.id}
                                            index={enigmaIndex}
                                          >
                                            {(enigmaDraggable) => (
                                              <div
                                                ref={enigmaDraggable.innerRef}
                                                {...enigmaDraggable.draggableProps}
                                              >
                                                <Box
                                                  className={`${classes.enigmaRow} ${isActiveEnigma ? classes.enigmaRowActive : ""}`}
                                                  {...enigmaDraggable.dragHandleProps}
                                                >
                                                  {/* Enigma link */}
                                                  <Link
                                                    to={`/enigme?tourId=${tourId}&zoneId=${zone.id}&enigmaId=${enigma.id}`}
                                                    className={classes.enigmaLink}
                                                    style={{ textDecoration: "none" }}
                                                  >
                                                    <Typography className={classes.enigmaNumber}>
                                                      {enigmaIndex + 1}
                                                    </Typography>
                                                    <Typography
                                                      className={`${classes.enigmaTitle} ${isActiveEnigma ? classes.enigmaTitleActive : ""}`}
                                                      title={enigma.title}
                                                    >
                                                      {enigma.title || t("tourHierarchy.unnamed_enigma")}
                                                    </Typography>
                                                  </Link>

                                                  {/* Delete enigma (hover only) */}
                                                  <Box
                                                    className={classes.enigmaDeleteButton}
                                                    onClick={(e) => {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      openDeleteDialog("enigma", enigma.id, zone.id);
                                                    }}
                                                  >
                                                    <DeleteOutlineIcon style={{ fontSize: 14 }} />
                                                  </Box>
                                                </Box>
                                              </div>
                                            )}
                                          </Draggable>
                                        );
                                      })}
                                      {enigmaDroppable.placeholder}
                                    </div>
                                  )}
                                </Droppable>

                                <Link
                                  to={
                                    canAddEnigma
                                      ? `/enigme?tourId=${tourId}&zoneId=${zone.id}`
                                      : "#"
                                  }
                                  className={`${classes.addEnigmaLink} ${!canAddEnigma ? classes.addEnigmaDisabled : ""}`}
                                  style={{ textDecoration: "none" }}
                                  onClick={(e) => {
                                    if (!canAddEnigma) e.preventDefault();
                                  }}
                                >
                                  <AddIcon style={{ fontSize: 13 }} />
                                  <span>{t("tourHierarchy.add_enigma")}</span>
                                </Link>
                              </Box>
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Box>

      {/* ---- Footer: add zone ---- */}
      <Box className={classes.footer}>
        <Button
          component={canAddZone ? Link : "button"}
          to={canAddZone ? `/zone?tourId=${tourId}` : undefined}
          disabled={!canAddZone}
          className={`${classes.addZoneButton} ${!canAddZone ? classes.addZoneButtonDisabled : ""}`}
          startIcon={<AddIcon style={{ fontSize: 15 }} />}
        >
          {t("tourHierarchy.add_zone")}
        </Button>
      </Box>

      {/* ---- Delete confirmation dialog ---- */}
      <DialogWarning
        open={deleteDialog.open}
        primaryText={
          deleteDialog.type === "zone"
            ? t("newTour.delete_zone_warning")
            : t("newZone.primaryTextDelete")
        }
        secondaryText={
          deleteDialog.type === "zone"
            ? t("newTour.delete_zone_confirm")
            : t("newZone.secondaryTextDelete")
        }
        primaryAction={handleConfirmDelete}
        closeAction={closeDeleteDialog}
        idItem={deleteDialog.id}
        primaryButtonText={t("parcours_list.delete_button")}
        secondaryButtonText={t("parcours_list.cancel_button")}
      />
    </Box>
  );
}

export default TourHierarchyComponent;
